import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, useTheme } from "@material-ui/core";
import {
    checkMaterialsDeleteStatus,
    checkUploadedMaterialsStatus,
    fetchValidationReport,
    getMaterialDetailsById,
    getMaterialsProcessingStatus,
    getPreflightReportStatus,
} from "store/features/materialSpecs/specs";
import { useDispatch, useSelector } from "react-redux";
import { ValidationInfo } from "pages/MaterialSpecs/components/InfoPanel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import InfoIcon from "@material-ui/icons/Info";
import WarningIcon from "@material-ui/icons/Warning";
import { useProcessingStatus } from "hooks";
import { isEmpty } from "utils";
import { useMaterialSpecs } from "pages/MaterialSpecs/hooks";

interface ValidationProps {
    showIcons?: boolean;
    disableInfo?: boolean;
    simplified?: boolean;
}

export const Validation = ({ disableInfo = false, simplified = false, showIcons = true }: ValidationProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { palette } = useTheme();
    const { hasError } = useSelector(getMaterialsProcessingStatus);
    const { selectedMaterialSpecId, selectedMaterialId } = useMaterialSpecs();
    const materialDetails = useSelector(getMaterialDetailsById(selectedMaterialSpecId, selectedMaterialId));
    useProcessingStatus(false, hasError);
    const isAnyMaterialUploading = useSelector(checkUploadedMaterialsStatus);
    const isAnyMaterialDeleting = useSelector(checkMaterialsDeleteStatus);
    const isPreflightLoading = useSelector(getPreflightReportStatus);

    useEffect(() => {
        if (
            materialDetails?.inputFileName &&
            selectedMaterialId &&
            !materialDetails?.preflightReport &&
            !isAnyMaterialUploading &&
            !isAnyMaterialDeleting &&
            !isPreflightLoading
        ) {
            dispatch(fetchValidationReport.request(selectedMaterialId));
        }
        // eslint-disable-next-line
    }, [selectedMaterialId, isPreflightLoading]);

    if (!materialDetails || !materialDetails.preflightReport) {
        return null;
    }

    if (isEmpty(materialDetails.preflightReport)) {
        return (
            <Typography component="div" variant="subtitle1">
                <Box
                    pb={2}
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    color={palette.warning.main}
                    fontWeight="fontWeightBold">
                    <Box display="flex" pr={2}>
                        <WarningIcon color="inherit" />
                    </Box>
                    {t("campaign.errors.preflightMissingMetadata")}
                </Box>
            </Typography>
        );
    }

    const { errors, info, warnings } = materialDetails.preflightReport;
    const hasErrors = errors.length !== 0;
    const hasWarnings = warnings.length !== 0;
    const hasInfo = info.length !== 0;

    return (
        <>
            <ValidationInfo
                simplified={simplified}
                defaultExpanded
                info={materialDetails.preflightReport.errors}
                header={
                    <>
                        {showIcons && (
                            <Box pr={2} display="flex" color={hasErrors ? palette.error.main : palette.success.main}>
                                {hasErrors ? <ErrorIcon color="inherit" /> : <CheckCircleIcon color="inherit" />}
                            </Box>
                        )}
                        <Typography component="div" variant="body2">
                            <Box
                                fontWeight="fontWeightBold"
                                color={hasErrors ? palette.error.main : palette.success.main}>
                                {/*TODO: add translations*/}
                                {hasErrors ? t("campaign.label.errors") : t("campaign.info.validationPassed")}
                            </Box>
                        </Typography>
                    </>
                }
            />
            {hasWarnings && (
                <ValidationInfo
                    simplified={simplified}
                    info={materialDetails.preflightReport.warnings}
                    header={
                        <>
                            {showIcons && (
                                <Box pr={2} display="flex" color={palette.warning.main}>
                                    <WarningIcon color="inherit" />
                                </Box>
                            )}
                            <Typography component="div" variant="body2">
                                <Box fontWeight="fontWeightBold" color={palette.warning.main}>
                                    {/*TODO: add translations*/}
                                    {t("campaign.label.warnings")}
                                </Box>
                            </Typography>
                        </>
                    }
                />
            )}
            {hasInfo && !disableInfo && (
                <ValidationInfo
                    simplified={simplified}
                    defaultExpanded
                    info={materialDetails.preflightReport.info}
                    header={
                        <>
                            {showIcons && (
                                <Box pr={2} display="flex">
                                    <InfoIcon color="primary" />
                                </Box>
                            )}
                            <Typography component="div" variant="body2">
                                <Box fontWeight="fontWeightBold" color={palette.info.main}>
                                    {/*TODO: add translations*/}
                                    {t("campaign.label.info")}{" "}
                                </Box>
                            </Typography>
                        </>
                    }
                />
            )}
        </>
    );
};
